import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  RotateCcw,
  Mail,
  Inbox,
  MailOpen,
  Archive,
  Building2,
  Clock,
  ChevronRight,
  Loader2,
  Trash2,
  User,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  fetchContactSubmissions,
  updateContactSubmissionStatus,
  deleteContactSubmission,
} from '../services/contactSubmissionService';
import { Select } from '../components';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'read', label: 'Read' },
  { value: 'archived', label: 'Archived' },
];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const statusPill = (status) => {
  switch (status) {
    case 'new':
      return 'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/50';
    case 'read':
      return 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/10 dark:text-emerald-400 dark:border-emerald-900/50';
    case 'archived':
      return 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-[#31373f] dark:text-gray-300 dark:border-gray-700';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700';
  }
};

const ContactSubmissions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        search: searchTerm || undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        limit: 100,
      };
      const res = await fetchContactSubmissions(params);
      setRows(res.data || []);
      setStats(res.stats || {});
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.message || 'Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  // Debounce search so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleReset = () => {
    setSearchTerm('');
    setSelectedStatus('all');
  };

  // Open a row and, if it was still "new", mark it read so the inbox count drops.
  const toggleRow = async (row) => {
    const opening = expandedRow !== row.id;
    setExpandedRow(opening ? row.id : null);
    if (opening && row.status === 'new') {
      try {
        await updateContactSubmissionStatus(row.id, 'read');
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: 'read' } : r)));
        setStats((s) => ({ ...s, new: Math.max(0, s.new - 1), read: s.read + 1 }));
      } catch (e) {
        // Non-fatal — the details still show.
      }
    }
  };

  const changeStatus = async (row, status) => {
    try {
      await updateContactSubmissionStatus(row.id, status);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error('Could not update status');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete enquiry from ${row.name}?`)) return;
    try {
      await deleteContactSubmission(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success('Enquiry deleted');
      load();
    } catch (e) {
      toast.error('Could not delete enquiry');
    }
  };

  const statCards = useMemo(() => ([
    { title: 'ALL ENQUIRIES', count: stats.total || 0, subtitle: 'Everything received', color: 'blue', icon: Mail },
    { title: 'NEW', count: stats.new || 0, subtitle: 'Awaiting review', color: 'blue', icon: Inbox },
    { title: 'READ', count: stats.read || 0, subtitle: 'Seen by the team', color: 'emerald', icon: MailOpen },
    { title: 'ARCHIVED', count: stats.archived || 0, subtitle: 'Closed out', color: 'gray', icon: Archive },
  ]), [stats]);

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-500 font-sans">

      {/* Header */}
      <div>
        <h1 className="text-[26px] font-bold text-[#1E293B] dark:text-gray-200 tracking-tight">Contact Enquiries</h1>
        <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-1 max-w-2xl leading-relaxed">
          Enquiries submitted from the ServPatch landing-page contact form. Click a row to
          see the full customer details.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#262b33] p-4 rounded-xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <input
            type="text"
            placeholder="Search name, email, organisation, message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#F8FAFC] dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10 focus:border-[#2563EB] text-[14px] text-[#1E293B] dark:text-gray-200 transition-all"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select
            value={selectedStatus}
            onChange={(v) => setSelectedStatus(v)}
            options={STATUS_OPTIONS}
            className="w-[170px]"
          />
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] text-white text-[13px] font-bold rounded-lg hover:bg-blue-700 transition-all shadow-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, idx) => (
          <div
            key={idx}
            className={`bg-white dark:bg-[#262b33] p-4 rounded-xl border-l-4 shadow-sm border border-[#E2E8F0] dark:border-gray-800 hover:shadow-md transition-all group ${
              idx === 0 ? 'border-l-blue-500 ring-2 ring-blue-500/10 bg-blue-50/20' :
              card.color === 'emerald' ? 'border-l-emerald-500' :
              card.color === 'gray' ? 'border-l-gray-400' :
              'border-l-blue-500'
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className={`text-[11px] font-bold uppercase tracking-wider ${idx === 0 ? 'text-blue-600' : 'text-[#64748B] dark:text-gray-400'}`}>
                {card.title}
              </span>
              <card.icon className={`w-4 h-4 ${idx === 0 ? 'text-blue-500' : 'text-[#94A3B8] dark:text-gray-500 group-hover:text-gray-600'}`} />
            </div>
            <div className="text-[24px] font-bold text-[#1E293B] dark:text-gray-200">{card.count}</div>
            <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">{card.subtitle}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#262b33] rounded-xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#F8FAFC] dark:bg-[#31373f] border-b border-[#E2E8F0] dark:border-gray-800">
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Received</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Organisation</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Service</th>
                <th className="px-6 py-4 text-left text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9] dark:divide-gray-800">
              {loading && (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center text-[#64748B] dark:text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
                    Loading enquiries...
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-red-600 font-semibold">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center text-[#64748B] dark:text-gray-400">
                    No contact enquiries yet.
                  </td>
                </tr>
              )}
              {!loading && !error && rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    className={`hover:bg-[#F8FAFC] dark:hover:bg-[#31373f] transition-colors cursor-pointer ${expandedRow === row.id ? 'bg-[#F8FAFC] dark:bg-[#31373f]' : ''}`}
                    onClick={() => toggleRow(row)}
                  >
                    <td className="px-6 py-5 align-top">
                      <div className="text-[14px] font-bold text-blue-600">{formatDate(row.created_at)}</div>
                      <div className="text-[12px] text-[#64748B] dark:text-gray-400 font-medium mt-1 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        {formatTime(row.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg shrink-0 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-[14px] font-bold text-[#1E293B] dark:text-gray-200 leading-tight">{row.name}</div>
                          <a
                            href={`mailto:${row.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[12px] text-blue-600 hover:underline mt-1 inline-block font-medium"
                          >
                            {row.email}
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="text-[14px] font-bold text-[#475569] dark:text-gray-400 flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-[#94A3B8]" />
                        {row.organisation || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="text-[13px] font-semibold text-[#475569] dark:text-gray-300">{row.service_type || '—'}</div>
                      <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1">{row.service_size || ''}</div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wider ${statusPill(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right align-top">
                      <button className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-[#31373f] transition-all ${expandedRow === row.id ? 'rotate-90 bg-gray-100 dark:bg-[#31373f]' : ''}`}>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8] dark:text-gray-500" />
                      </button>
                    </td>
                  </tr>

                  {expandedRow === row.id && (
                    <tr>
                      <td colSpan="6" className="px-8 py-6 bg-[#F8FAFC] dark:bg-[#20242c] border-t border-b border-[#E2E8F0] dark:border-gray-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="space-y-3">
                            <h4 className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200">Customer details</h4>
                            <div className="bg-white dark:bg-[#262b33] p-4 rounded-lg border border-[#E2E8F0] dark:border-gray-800 text-[13px] text-[#475569] dark:text-gray-300 space-y-2 shadow-sm">
                              {[
                                ['Name', row.name],
                                ['Email', row.email],
                                ['Organisation', row.organisation],
                                ['Service type', row.service_type],
                                ['Service size', row.service_size],
                              ].map(([label, value]) => (
                                <div key={label} className="flex gap-4">
                                  <span className="font-bold text-[#64748B] dark:text-gray-400 w-32 shrink-0">{label}</span>
                                  <span className="break-all">{value || '—'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-3">
                            <h4 className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200">What they'd like to improve first</h4>
                            <div className="bg-white dark:bg-[#262b33] p-4 rounded-lg border border-[#E2E8F0] dark:border-gray-800 text-[13px] text-[#475569] dark:text-gray-300 leading-relaxed shadow-sm min-h-[80px] whitespace-pre-wrap">
                              {row.message || 'No message provided.'}
                            </div>
                            <div className="flex flex-wrap gap-2 pt-1">
                              <a
                                href={`mailto:${row.email}`}
                                className="flex items-center gap-1.5 px-3 py-2 bg-[#2563EB] text-white text-[12px] font-bold rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                              >
                                <Mail className="w-3.5 h-3.5" /> Reply
                              </a>
                              {row.status !== 'archived' ? (
                                <button
                                  onClick={() => changeStatus(row, 'archived')}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 text-[#475569] dark:text-gray-300 text-[12px] font-semibold rounded-lg hover:bg-gray-50 transition-all"
                                >
                                  <Archive className="w-3.5 h-3.5" /> Archive
                                </button>
                              ) : (
                                <button
                                  onClick={() => changeStatus(row, 'read')}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-[#31373f] border border-[#E2E8F0] dark:border-gray-700 text-[#475569] dark:text-gray-300 text-[12px] font-semibold rounded-lg hover:bg-gray-50 transition-all"
                                >
                                  <MailOpen className="w-3.5 h-3.5" /> Restore
                                </button>
                              )}
                              <button
                                onClick={() => remove(row)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-[#31373f] border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 text-[12px] font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-all"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContactSubmissions;
